import * as yup from 'yup';

import getConfig from 'next/config';


export const postalCodeRegex = /^[A-Za-z]\d[A-Za-z][ ]?\d[A-Za-z]\d$/;

export const zipCodeRegex = /^\d{5}(?:[-\s]\d{4})?$/;

export const codeRegex = /(^[A-Za-z]\d[A-Za-z][ ]?\d[A-Za-z]\d$|^\d{5}(?:[-\s]\d{4})?$|^[0-9]{4}$)/;

export const postcodeRegex = /^[0-9]{4}$/;

export const guestNameRegex = /^\s*[a-zA-Z\u00C0-\u00FF\-./&\s]+\s?$/;

export const phoneRegExp = /^(\+?\d{1,4}[\s-]?)?(\(?\d{3}\)?[\s-]?)?[\d\s-]{7,10}$/;

export const folioFileNameRegex = /^[a-z0-9_.,\-\s\\(\\)]+$/i;

export const splitGuestNameOnFirstWhitespace = (guestName = '') => {
  const [firstName = '', lastName = ''] = guestName.split(/\s+/, 2);
  return { firstName, lastName };
};

export const fileToImageType = (file, uploadedImagesLength = 0) =>
  new Promise((resolve, reject) => {
    if (/\.(jpe?g|png|gif)$/i.test(file.name)) {
      const fileReader = new FileReader();
      fileReader.addEventListener(
        'load',
        async () => {
          const image = {
            desc: '',
            id: new Date().getTime() * Math.floor(Math.random() + 10),
            title: file.name.split('.')[0],
            src: fileReader.result,
            href: fileReader.result,
            order: uploadedImagesLength + 1,
            isNew: true,
            fileInfo: {
              name: file.name
            }
          };

          resolve(image);
        },
        false
      );

      fileReader.readAsDataURL(file);
    } else {
      reject();
    }
  });

export const isNumeric = (str) => {
  if (typeof str !== 'string') return false;
  return !Number.isNaN(str) && !Number.isNaN(parseFloat(str));
};

export const phoneFormatted = (phone, alt = 'N/A') => {
  if (!phone) return alt;
  if (phone.length === 10 && isNumeric(phone)) return `1 (${phone.slice(0, 3)}) ${phone.slice(3, 6)}-${phone.slice(6)}`;
  return phone;
};

export const sanitizePhone = (phone, alt = 'N/A') => {
  if (!phone) return alt;
  if (phone.length === 11) return `1 (${phone.slice(1, 4)}) ${phone.slice(4, 7)}-${phone.slice(7)}`;
  if (phone.length === 10) return phoneFormatted(phone);
  return phone;
};

/**
 * Cleans a string phone number from the `FormElements.PhoneNumber` input component.
 * Takes a phone number of the format `1 (XXX) XXX-XXXX` and trims it down to `1XXXXXXXXXX`.
 * @param {string} phoneNumber
 * @returns {string} The cleaned phone number, ready to be saved to the db
 */
export const cleanPhoneNumber = (phoneNumber) => phoneNumber.replace(/[^0-9]/g, '');

export const getInitials = (firstName, lastName) => {
  let initials = '??';
  if (firstName && lastName && firstName.length > 0 && lastName.length > 0) {
    initials = `${firstName.slice(0, 1)}${lastName.slice(0, 1)}`;
  } else if (firstName && !lastName && firstName.length > 0) {
    initials = `${firstName.slice(0, 1)}`;
  } else if (lastName && !firstName && lastName.length > 0) {
    initials = `${lastName.slice(0, 1)}`;
  }
  return initials;
};

// There might be a way to get the Graphql error without this prefix... but i couldn't find it.
const gqlErrorPrefixString = 'GraphQL error: ';
export const stripGraphqlErrorPrefix = (errorString = '') =>
  errorString.startsWith(gqlErrorPrefixString) ? errorString.substring(gqlErrorPrefixString.length) : errorString;

export const addCommas = (number) => {
  let newNumber = number;
  if (number.toString().includes(',')) {
    newNumber = number.toString().replaceAll(',', '');
  }
  const parts = newNumber.toString().split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return parts.join('.');
};

export const roundTo = (number, precision = 2) =>
  Math.round((+number + Number.EPSILON) * 10 ** precision) / 10 ** precision;

export const roundToFixed = (number, precision = 2) => roundTo(number, precision).toFixed(precision);

export const formatCurrency = (number, precision = 2, symbol = '$', pos = 'Before') => {
  const formatted = addCommas(roundToFixed(number, precision));
  return pos === 'Before' ? `${symbol}${formatted}` : `${formatted}${symbol}`;
};

export const formatString = (inputStr, formatArgs = {}, transform = (str) => str) => {
  const formatRegex = /[^\\]{([^{}]*[^\\])}/g;
  let finalString = inputStr;
  let match;

  do {
    match = formatRegex.exec(inputStr);
    if (!match) break;
    const [, name] = match;
    const replacement = transform(formatArgs[name] || '');
    finalString = finalString.replace(`{${name}}`, replacement);
  } while (match);
  return finalString;
};

export const formatURL = (urlFormat, params) => formatString(urlFormat.trim(), params, encodeURIComponent);

/**
 * Remove the query string (i.e. `?someParam=123`) and anchor link (i.e. `#tab-1`) from the URL if its present
 * @param {*} router Next.js Router
 */
export const resetURL = (router) => {
  const path = router?.asPath ?? '';
  const queryMatcher = /\?[^$#]+/i;
  const anchorMatcher = /#[^$]+/i;
  if (path.match(queryMatcher)) router.push({ pathname: path.replace(queryMatcher, '').replace(anchorMatcher, '') });
};

/**
 * Format a rate value for the rate card table.
 * Values in the rate card table can be a percent or dollar amount. This formats the value for display depending on it's type.
 * @param {string} rateType
 * @param {int} value
 * @param {string} currencyName
 * @returns
 */
export const formatRateCardValue = (rateType, value) => {
  if (rateType === 'Percent') {
    return `${value}%`;
  }

  return `${value}`;
};

/**
 * Add or remove an element from an array.
 * @param {array} arr
 * @param {*} item
 * @returns array
 */
export const addOrRemove = (arr, item) => (arr.includes(item) ? arr.filter((i) => i !== item) : [...arr, item]);

/**
 * Formats a string for the payment method of a booking.
 * @param {string} method The payment method string; either `CreditCard` or `PaymentCode`
 * @param {string} token The stripe token for the Credit Card
 * @param {PaymentCode} code The payment code object; must have the `code` property
 * @returns The formatted string
 */
export const createPaymentMethodString = (method, token, code) =>
  method === 'CreditCard'
    ? `Credit Card / ${token ? 'Has Stripe Token' : 'No Token'}`
    : `Payment ID / ${code ? code.code : 'No Payment ID Set'}`;

/**
 * @param {string} input
 * JSON.parse, but prevent throws when the input string isn't valid JSON
 */
export const parseJson = (input) => {
  if (!input) return {};
  try {
    return JSON.parse(input);
  } catch (error) {
    return {};
  }
};

/**
 * Used as a filter to return unique values in an array
 * @param {Any} element
 * @param {Number} index
 * @param {Array[Any]} array
 * @return {Boolean}
 */
export const filterUnique = (element, index, array) =>
  element !== undefined && element !== null && array.indexOf(element) === index;

/**
 * Used as a filter to return unique Entities in an array by id.
 * @param element any
 * @param index number
 * @param array: any[]
 */
export const filterUniqueById = (element, index, array) =>
  array.findIndex(({ id = 0 }) => id === element?.id ?? 0) === index;

/**
 * Used as a filter to return unique Entities in an array by name.
 * This function takes the first found index of unique objects by property.
 * @param element any
 * @param index number
 * @param array: any[]
 */
export const filterUniqueByName = (element, index, array) =>
  array.findIndex(({ name = '' }) => name === element?.name ?? '') === index;

/**
 * This utility function takes  an array of string and replaces an index with another array.
 * @example
 * // returns ['Draft', 'Rejected', 'Removed', 'Unconfirmed']
 * replaceWith(['Unconfirmed'], 'Unconfirmed', ['Draft', 'Rejected', 'Removed', 'Unconfirmed'])
 * @param {string[]} arr - a string array
 * @param {string | number} replacee - a string we want to replace with an array of other string values
 * @param {any[] | string | number | null} replacer - an array of strings that will replace a string in a given array
 * @returns {string[]} A copy of the original object with the replacer array swapped in at the replacee index
 */
export const replaceWith = (arr = [], replacee = '', replacer = []) => {
  const replaceIndex = arr.indexOf(replacee);
  if (replaceIndex === -1) return arr;
  const clone = [...arr];
  clone[replaceIndex] = replacer;
  return clone.flat();
};

/**
 * this utility function will check for a match between two strings without case sensitivity
 * @param {string} input - a string input we want to compare with a specified string
 * @param {string} compare - a string we want the input to be compared to
 * @returns
 */

export const stringCheckCaseInsensitive = (input, compare) =>
  input.localeCompare(compare, undefined, { sensitivity: 'base' }) === 0;

/**
 * Convert a string to camelCaseFormat
 */
export const camelCase = (string = '', opts = { retainCase: false }) => {
  if (!string || !string?.length || typeof string !== 'string') return string;

  const { retainCase = false } = opts || {};

  const words = string
    .replace(/[^a-z0-9\s]+/gi, '')
    .trim()
    .split(' ');

  return words
    .map((word, i) => {
      const [firstLetter, ...letters] = word;
      return `${i === 0 ? firstLetter.toLowerCase() : firstLetter.toUpperCase()}${
        !retainCase ? letters.join('').toLowerCase() : letters.join('')
      }`;
    })
    .join('');
};

/**
 * Compare two or more objects to see if they contain the same keys and values
 */
export const isEqual = (...objects) => {
  // Sort the object by their key, and stringify
  const json = objects.map((unsortedObj) => {
    const sortedObject = Object.keys(unsortedObj)
      .sort()
      .reduce((newObj, key) => ({ ...newObj, [key]: unsortedObj[key] }), {});

    return JSON.stringify(sortedObject);
  });

  // See that subsequent strings are the same as the first one
  return json.every((str) => str === json[0]);
};

/**
 * Format a tax/fee amount as a string with the appropriate adornment.
 * @param {TaxFee} taxFee
 * @returns {string} Formatted amount string
 * @example
 * formatTaxFeeAmount({amount: 5, taxFeeType: 'Percent', ...})
 * // returns '5%'
 * @example
 * formatTaxFeeAmount({amount: 2.99, taxFeeType: 'Flat', ...})
 * // returns '$2.99'
 */
export const formatTaxFeeAmount = (taxFee) => {
  const { amount = 0, taxFeeType } = taxFee;

  return addCommas(
    taxFeeType === 'Percent' ? `${Number.isInteger(amount) ? amount.toFixed(1) : amount}%` : `$${amount.toFixed(2)}`
  );
};

/**
 * Sorts an array or an array of objects alphabetically.
 *
 * @param {String} type The type of the item to be sorted. It can be 'arrayObjects' for an array of objects or 'array' for an array.
 * @param {Array} item The array or array of objects to be sorted.
 * @param {String} key The key in the objects to sort by. This parameter is required when type is 'arrayObjects'.
 * @returns {Array} The sorted array or array of objects.
 */

export const sortAlphabetically = (type, item, key) => {
  const compareItemsWithKey = (a, b) => {
    if (a[key] < b[key]) return -1;
    if (a[key] > b[key]) return 1;
    return 0;
  };

  const compareItems = (a, b) => {
    if (a < b) return -1;
    if (a > b) return 1;
    return 0;
  };

  switch (type) {
    case 'arrayObjects':
      return item.sort(compareItemsWithKey);
    case 'array':
      return item.sort(compareItems);
    default:
      throw new Error(`Invalid type: ${type}. Expected 'array' or 'arrayObjects'.`);
  }
};

export const removeDuplicateObjects = (objects, field) => {
  const seenIds = new Set();
  return objects.filter((obj) => {
    if (!seenIds.has(obj[field])) {
      seenIds.add(obj[field]);
      return true;
    }
    return false;
  });
};

export function countObjectsById(array) {
  const counts = {};
  array.forEach((obj) => {
    if (obj.id in counts) {
      counts[obj.id] += 1;
    } else {
      counts[obj.id] = 1;
    }
  });
  return counts;
}

export const calculateBookingCheckboxState = (selectionDetails) => {
  const { isAllSelected, selected, excluded, guestDayCount: maximumBookingGuestDayCount } = selectionDetails ?? {};
  const selectedGuestDaysCount = selected.flatMap((roomNight) => roomNight.split(',')).length;
  const excludedGuestDaysCount = excluded.flatMap((roomNight) => roomNight.split(',')).length;
  const isChecked = (isAllSelected && excluded.length === 0) || selectedGuestDaysCount === maximumBookingGuestDayCount;
  const isAllManuallyExcluded = excludedGuestDaysCount === maximumBookingGuestDayCount;
  const isPartialSelection =
    (isAllSelected && excluded.length > 0 && !isAllManuallyExcluded) || (!isAllSelected && selected.length > 0);
  const isIndeterminate = isPartialSelection && !isChecked;

  return { isChecked, isIndeterminate };
};

export const getDayDifference = (overrides, dayNum) => {
  const override = overrides.find((day) => day.dayOfWeek === dayNum);
  if (override) {
    return override.difference;
  }
  return '';
};

// Method to deep copy an Array of Objects
export const deepCopyArray = (inObject) => {
  if (typeof inObject !== 'object' || inObject === null) {
    return inObject; // Return the value if inObject is not an object
  }

  // Create an array or object to hold the values
  const outObject = Array.isArray(inObject) ? [] : {};

  // eslint-disable-next-line guard-for-in
  Object.keys(inObject).forEach((key) => {
    const value = inObject[key];
    // Recursively copy for nested objects, including arrays
    outObject[key] = deepCopyArray(value);
  });

  return outObject;
};

/**
 * Sorts an array from Apollo Client (which may be frozen) with configurable options.
 * Creates a defensive copy to avoid mutation errors and applies sorting with various options.
 *
 * @param {Array} array - Array to sort (potentially frozen from Apollo)
 * @param {Object} options - Sort configuration options
 * @param {string} [options.key] - Object key to sort by (if sorting array of objects)
 * @param {string} [options.order='asc'] - Sort order ('asc' or 'desc')
 * @param {boolean} [options.compareLocale=false] - Whether to use locale comparison for strings
 * @returns {Array} A new sorted array
 *
 * @example
 * // Sort an array of objects by id
 * const sortedUsers = apolloSort(users, { key: 'id' });
 *
 * @example
 * // Sort an array of strings in descending order
 * const sortedNames = apolloSort(names, { order: 'desc' });
 *
 * @example
 * // Sort with locale-aware comparison
 * const sortedLabels = apolloSort(labels, { compareLocale: true });
 */
export const apolloSort = (array, options = {}) => {
  // Default options
  const { key, order = 'asc', compareLocale = false } = options;

  // Return early for empty arrays or non-arrays
  if (!array || !Array.isArray(array) || array.length === 0) {
    return array || [];
  }

  // Create a defensive copy to avoid mutating frozen objects
  const copy = [...array];

  // Sort the copy
  copy.sort((a, b) => {
    // Get values to compare (either direct or from specified key)
    const aValue = key ? a[key] : a;
    const bValue = key ? b[key] : b;

    let comparison = 0;

    // Compare based on locale or direct comparison
    if (compareLocale) {
      comparison = String(aValue).localeCompare(String(bValue));
    } else if (aValue < bValue) {
      comparison = -1;
    } else if (aValue > bValue) {
      comparison = 1;
    }
    // If equal, comparison remains 0

    // Apply sort direction
    return order === 'asc' ? comparison : -comparison;
  });

  return copy;
};

export const getAllRouteMatches = (arr) => {
  const result = [];

  const processRoutes = (routes) => {
    routes?.forEach(({ routeMatches, subMenu }) => {
      if (Array.isArray(routeMatches)) {
        result.push(...routeMatches);
      }

      if (Array.isArray(subMenu)) {
        processRoutes(subMenu);
      }
    });
  };

  processRoutes(arr);
  return result;
};

export const capitalizeWords = (str = '') =>
  str
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

/**
 * Function to check if any item in the array is included in the string
 * @param {*} str
 * @param {*} arr
 * @returns boolean
 */
export const containsAnySubstring = (str, arr) => arr.some((item) => str.includes(item));

export const getAlphabeticCharacters = (str) => str.replace(/[^a-zA-Z]/g, '');

export const noSpecialChars = () =>
  yup.string().test({
    name: 'noSpecialChars',
    exclusive: false,
    message: 'No special characters allowed',
    test: (value) => !/[!@#$%^&*(),.?":{}|<>]/.test(value)
  });

export const checkXnumberOfDecimalPlaces = (x = 6) =>
  yup.number().test({
    name: 'checkXnumberOfDecimalPlaces',
    exclusive: false,
    message: `Decimal places should not exceed ${x}`,
    test: (value) => {
      const decimalPlaces = value?.toString()?.split('.')[1]?.length ?? 0;
      return decimalPlaces <= x;
    }
  });

/**
 * Checks if an array contains duplicate values.
 *
 * @param {Array} arr The array to be checked for duplicates.
 * @returns {Boolean} Returns `true` if the array has duplicate values, otherwise `false`.
 */
export const arrayHasDuplicates = (arr) => new Set(arr).size !== arr.length;

export const findPayingOrgId = ({ bookingOrganization, payingOrganization }) => {
  const { id: organizationId = 0 } = bookingOrganization ?? {};
  const { id: paymentCodeOrganizationId = 0 } = payingOrganization ?? {};
  return paymentCodeOrganizationId || organizationId;
};


const {
  publicRuntimeConfig: { AUTH_HUB_URL, FALLBACK_TOKEN }
} = getConfig();

/**
 * Utility function to fetch token from Azure endpoint
 * Can be used in both React components and service functions
 * @param {Object} options - Additional fetch options
 * @returns {Promise<{accessToken: string}>} - The fetched token response
 */
export const fetchTokenFromAzure = async (options = {}) => {
  try {
    const response = await fetch(`${AUTH_HUB_URL}/api/token`, {
      method: 'GET',
      credentials: 'include', // Send cookies and auth headers cross-origin
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      },
      ...options
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch token: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();

    // Handle different response formats and extract token
    const token = data.token || data.access_token || data.accessToken || data;

    if (!token) {
      throw new Error('No token found in response');
    }

    return {
      accessToken: token
    };
  } catch (error) {
    /* eslint-disable no-console */
    console.error('Error fetching token from Azure:', error);
    /* eslint-disable no-console */
    throw error;
  }
};

/**
 * Utility function to fetch token with fallback
 * @param {Object} options - Additional fetch options
 * @returns {Promise<{accessToken: string}>} - The fetched token response or fallback
 */
export const fetchTokenWithFallback = async (options = {}) => {
  console.log('fetchTokenWithFallback');
  try {
    return await fetchTokenFromAzure(options);
  } catch (error) {
    console.warn('Failed to fetch token from Azure, using fallback:', error.message);
    return {
      accessToken: FALLBACK_TOKEN
    };
  }
};

